import {useCallback, useMemo, useRef, useState} from "react";
import {useMutation, useQueryClient} from "react-query";
import Alert from "react-s-alert";

import {TableUserDto} from "../../../../../utils/types/user";
import {
  QuicksightDashboard,
  QuicksightService,
} from "../../../../../services/QuicksightService";
import {buildResponseErrorMessage} from "../../../../../services/api/errorHelpers";
import {
  getQuicksightUsersDashboardsQueryKey,
  QuicksightUsersDashboardsData,
  useQuicksightUsersDashboards,
} from "../../../hooks/useQuicksightUsersDashboards";
import {
  applyBulkDashboardCategoryToggle,
  applyBulkDashboardToggle,
  buildUserDashboardsCacheValue,
  countBulkPendingDashboardChanges,
  DashboardIdsByEmail,
  diffBulkDashboardChanges,
  isBulkDashboardPending,
} from "../bulkQuicksightDashboards.helpers";

const EMPTY_DASHBOARDS: ReadonlyArray<{id: string; name: string}> = [];

type Params = Readonly<{
  users: ReadonlyArray<TableUserDto>;
  onDashboardsChange?: (dashboardsByEmail: DashboardIdsByEmail) => void;
}>;

const dashboardIdsByEmailFromAssigned = (
  emails: ReadonlyArray<string>,
  dashboardsByEmail: Readonly<
    Record<string, ReadonlyArray<QuicksightDashboard>>
  >,
): DashboardIdsByEmail =>
  Object.fromEntries(
    emails.map(email => [
      email,
      (dashboardsByEmail[email] ?? []).map(dashboard => dashboard.id),
    ]),
  );

export const useBulkQuicksightDashboards = ({
  users,
  onDashboardsChange,
}: Params) => {
  const queryClient = useQueryClient();
  const usersWithEmail = useMemo(
    () =>
      users.filter(
        (user): user is TableUserDto & {id: string; email: string} =>
          Boolean(user.id && user.email),
      ),
    [users],
  );
  const emails = useMemo(
    () => usersWithEmail.map(user => user.email),
    [usersWithEmail],
  );
  const emailByUserId = useMemo(
    () =>
      Object.fromEntries(
        usersWithEmail.map(user => [user.id, user.email]),
      ) as Record<string, string>,
    [usersWithEmail],
  );

  const userDashboardsQuery = useQuicksightUsersDashboards(emails);
  const dashboardOptions =
    userDashboardsQuery.data?.dashboardOptions ?? EMPTY_DASHBOARDS;
  const dashboardIds = useMemo(
    () =>
      dashboardOptions
        .map(dashboard => dashboard.id)
        .filter((id): id is string => Boolean(id)),
    [dashboardOptions],
  );

  const savedDashboardsByEmail = useMemo(
    () =>
      dashboardIdsByEmailFromAssigned(
        emails,
        userDashboardsQuery.data?.dashboardsByEmail ?? {},
      ),
    [emails, userDashboardsQuery.data],
  );

  const onDashboardsChangeRef = useRef(onDashboardsChange);
  onDashboardsChangeRef.current = onDashboardsChange;
  const [draftDashboardsByEmail, setDraftDashboardsByEmail] =
    useState<DashboardIdsByEmail | null>(null);
  const currentDashboardsByEmail =
    draftDashboardsByEmail ?? savedDashboardsByEmail;

  const isLoading =
    userDashboardsQuery.isLoading || userDashboardsQuery.isFetching;
  const isError = Boolean(userDashboardsQuery.isError);
  const isDashboardsDataReady =
    emails.length === 0 || userDashboardsQuery.isSuccess;

  const notifyDashboardsChange = useCallback(
    (dashboardsByEmail: DashboardIdsByEmail) => {
      if (isDashboardsDataReady) {
        onDashboardsChangeRef.current?.(dashboardsByEmail);
      }
    },
    [isDashboardsDataReady],
  );

  const pendingChangeCount = useMemo(
    () =>
      draftDashboardsByEmail
        ? countBulkPendingDashboardChanges(
            savedDashboardsByEmail,
            draftDashboardsByEmail,
            emails,
          )
        : 0,
    [draftDashboardsByEmail, emails, savedDashboardsByEmail],
  );

  const hasDashboard = useCallback(
    (userId: string, dashboardId: string) => {
      const email = emailByUserId[userId];

      return Boolean(
        email && (currentDashboardsByEmail[email] ?? []).includes(dashboardId),
      );
    },
    [currentDashboardsByEmail, emailByUserId],
  );

  const isDashboardPending = useCallback(
    (dashboardId: string) =>
      Boolean(
        draftDashboardsByEmail &&
          isBulkDashboardPending(
            savedDashboardsByEmail,
            draftDashboardsByEmail,
            emails,
            dashboardId,
          ),
      ),
    [draftDashboardsByEmail, emails, savedDashboardsByEmail],
  );

  const toggleDashboard = useCallback(
    (dashboardId: string, grantToAll: boolean) => {
      setDraftDashboardsByEmail(current => {
        const next = applyBulkDashboardToggle(
          current ?? savedDashboardsByEmail,
          emails,
          dashboardId,
          grantToAll,
        );
        notifyDashboardsChange(next);
        return next;
      });
    },
    [emails, notifyDashboardsChange, savedDashboardsByEmail],
  );

  const toggleAllDashboards = useCallback(
    (grantToAll: boolean) => {
      setDraftDashboardsByEmail(current => {
        const next = applyBulkDashboardCategoryToggle(
          current ?? savedDashboardsByEmail,
          emails,
          dashboardIds,
          grantToAll,
        );
        notifyDashboardsChange(next);
        return next;
      });
    },
    [dashboardIds, emails, notifyDashboardsChange, savedDashboardsByEmail],
  );

  const discardChanges = useCallback(() => {
    setDraftDashboardsByEmail(null);
  }, []);

  const {mutateAsync, isLoading: isSaving} = useMutation(
    async () => {
      const nextDashboardsByEmail = currentDashboardsByEmail;
      const {granted, revoked} = diffBulkDashboardChanges(
        savedDashboardsByEmail,
        nextDashboardsByEmail,
        emails,
      );

      if (granted.length > 0) {
        await QuicksightService.putPermissionsBulk({
          selected: true,
          dashboardUsers: granted,
        });
      }

      if (revoked.length > 0) {
        await QuicksightService.putPermissionsBulk({
          selected: false,
          dashboardUsers: revoked,
        });
      }

      return nextDashboardsByEmail;
    },
    {
      onSuccess: nextDashboardsByEmail => {
        queryClient.setQueryData<QuicksightUsersDashboardsData>(
          getQuicksightUsersDashboardsQueryKey(emails),
          previous => ({
            dashboardOptions: previous?.dashboardOptions ?? dashboardOptions,
            dashboardsByEmail: Object.fromEntries(
              emails.map(email => [
                email,
                buildUserDashboardsCacheValue(
                  nextDashboardsByEmail[email] ?? [],
                  previous?.dashboardOptions ?? dashboardOptions,
                  previous?.dashboardsByEmail[email] ?? [],
                ),
              ]),
            ),
          }),
        );
        emails.forEach(email => {
          queryClient.setQueryData<ReadonlyArray<QuicksightDashboard>>(
            ["quicksightUserDashboards", email],
            previousDashboards =>
              buildUserDashboardsCacheValue(
                nextDashboardsByEmail[email] ?? [],
                dashboardOptions,
                previousDashboards ?? [],
              ),
          );
        });
        setDraftDashboardsByEmail(null);
      },
      onError: async (resp: unknown) => {
        Alert.warning(
          await buildResponseErrorMessage(
            resp,
            "Failed to save Quicksight dashboards for selected users",
          ),
        );
      },
    },
  );

  const save = useCallback(async () => {
    if (pendingChangeCount === 0) {
      return;
    }

    await mutateAsync();
  }, [mutateAsync, pendingChangeCount]);

  return {
    currentDashboardsByEmail,
    dashboardOptions,
    discardChanges,
    hasDashboard,
    isDashboardPending,
    isDashboardsDataReady,
    isError,
    isLoading,
    isSaving,
    pendingChangeCount,
    save,
    toggleAllDashboards,
    toggleDashboard,
  };
};
